import { useEffect, useState } from 'react';

import { FIELD_TYPE_OPTIONS, type FieldType } from '../../../schema/api';
import type { CandidateFieldResponse, ModifyCandidateFieldRequest } from '../api';

interface CandidateFieldEditModalProps {
  open: boolean;
  field: CandidateFieldResponse;
  busy?: boolean;
  onSubmit: (request: ModifyCandidateFieldRequest) => void;
  onCancel: () => void;
}

/** Modal launched from CandidateFieldCard's "Editar" action. */
export function CandidateFieldEditModal({
  open,
  field,
  busy = false,
  onSubmit,
  onCancel,
}: CandidateFieldEditModalProps): JSX.Element | null {
  const [functionalName, setFunctionalName] = useState<string>(field.functionalName);
  const [suggestedType, setSuggestedType] = useState<FieldType>(
    field.suggestedType as FieldType,
  );
  const [suggestedRequired, setSuggestedRequired] = useState<boolean>(field.suggestedRequired);
  const [isCritical, setIsCritical] = useState<boolean>(field.isCritical);

  useEffect(() => {
    if (!open) {
      return;
    }
    setFunctionalName(field.functionalName);
    setSuggestedType(field.suggestedType as FieldType);
    setSuggestedRequired(field.suggestedRequired);
    setIsCritical(field.isCritical);
  }, [open, field]);

  if (!open) {
    return null;
  }

  const handleSubmit = (): void => {
    const trimmedName = functionalName.trim();
    onSubmit({
      functionalName: trimmedName !== '' ? trimmedName : null,
      suggestedType,
      suggestedRequired,
      isCritical,
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-field-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) {
          onCancel();
        }
      }}
    >
      <div className="card w-full max-w-md p-4 shadow-lg sm:p-6">
        <h2 id="edit-field-title" className="text-lg font-semibold text-slate-900">
          Editar campo propuesto
        </h2>

        <div className="mt-4 space-y-4">
          <div>
            <label htmlFor="edit-field-name" className="label">
              Nombre funcional
            </label>
            <input
              id="edit-field-name"
              className="input"
              value={functionalName}
              onChange={(event) => setFunctionalName(event.target.value)}
              disabled={busy}
            />
          </div>

          <div>
            <label htmlFor="edit-field-type" className="label">
              Tipo sugerido
            </label>
            <select
              id="edit-field-type"
              className="input"
              value={suggestedType}
              onChange={(event) => setSuggestedType(event.target.value as FieldType)}
              disabled={busy}
            >
              {FIELD_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={suggestedRequired}
              onChange={(event) => setSuggestedRequired(event.target.checked)}
              disabled={busy}
            />
            Marcar como obligatorio
          </label>

          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={isCritical}
              onChange={(event) => setIsCritical(event.target.checked)}
              disabled={busy}
            />
            Marcar como crítico (bloquea revisión si baja de umbral)
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={busy}
          >
            {busy ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}
